import React, { useState } from 'react';
import { useAdsTrend } from '../hooks/useOverview';
import type { OverviewRange } from '../api/campaignsApi';
import { formatConversions } from '@/shared/utils/format';
import styles from './AdsTrendChart.module.css';

type Range = '7d' | '30d' | '90d';

interface AdsTrendChartProps {
  campaignId?: string;
  /** When set, the chart is driven by the parent's range and hides its own toggle. */
  range?: OverviewRange;
}

/**
 * Spend & Conversions trend chart. Renders a simple SVG bar chart driven by
 * /campaigns/overview/trend. Pass `campaignId` for per-campaign trend. Pass
 * `range` to control it from the page (Overview); otherwise it shows its own
 * 7d/30d/90d toggle (campaign detail).
 */
export function AdsTrendChart({ campaignId, range: controlledRange }: AdsTrendChartProps = {}) {
  const [localRange, setLocalRange] = useState<Range>('30d');
  const range: OverviewRange = controlledRange ?? localRange;
  const { data, isLoading } = useAdsTrend(range, campaignId);

  const points = data?.data ?? [];
  const maxSpend = Math.max(1, ...points.map((p) => p.spend));

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>Spend &amp; Conversions</span>
        {!controlledRange && (
          <div className={styles.toggle}>
            {(['7d', '30d', '90d'] as const).map((r) => (
              <button
                key={r}
                type="button"
                className={`${styles.toggleBtn} ${localRange === r ? styles.toggleBtnActive : ''}`}
                onClick={() => setLocalRange(r)}
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : points.length === 0 ? (
        <div className={styles.empty}>No data for the selected range.</div>
      ) : (
        <div className={styles.chart}>
          {points.map((p) => {
            const h = (p.spend / maxSpend) * 100;
            return (
              <div
                key={p.date}
                className={styles.bar}
                style={{ height: `${Math.max(h, 2)}%` }}
                title={`${p.date} — $${p.spend.toFixed(0)} · ${formatConversions(p.conversions)} conv`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
